import type { DownloadPort } from "~/lib/download";
import type { HttpPort } from "~/lib/http";

interface RawReport {
  class_name: string;
  summary: {
    total_students: number;
    total_checkins: number;
    total_checkouts: number;
    checkin_rate_percent: number;
    avg_mood: number | null;
    tree_level: number;
    new_praise_messages: number;
    unread_concerns: number;
  };
}

interface RawRecipient {
  id: string;
  name: string | null;
  email: string;
  created_at: string;
}

export interface ReportWeek {
  weekStart: string;
  weekNumber: number;
  label: string;
}

export interface ReportView {
  weeks: ReportWeek[];
  selectedWeek: ReportWeek;
  className: string;
  empty: boolean;
  summary: {
    totalStudents: number;
    totalCheckins: number;
    totalCheckouts: number;
    checkinRatePercent: number;
    averageMood: number | null;
    moodLabel: string;
    treeLevel: number;
    praiseMessages: number;
    unreadConcerns: number;
  };
}

export interface ReportRecipient {
  id: string;
  name: string | null;
  email: string;
}

export type ReportCommand =
  | { type: "download"; weekStart: string }
  | { type: "add-recipient"; name: string; email: string }
  | { type: "delete-recipient"; recipientId: string }
  | { type: "send"; weekStart: string; weekNumber: number; recipients: ReportRecipient[] };

export type ReportCommandResult =
  | { type: "downloaded"; filename: string }
  | { type: "recipient-added"; recipient: ReportRecipient }
  | { type: "recipient-deleted" }
  | { type: "sent"; sent: number; message: string; placeholder: boolean };

export function weekNumber(dateString: string) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const firstWeek = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - firstWeek.getTime()) / 86400000 - 3 + (firstWeek.getDay() + 6) % 7) / 7);
}

export function lastFourWeeks(now: Date): ReportWeek[] {
  return Array.from({ length: 4 }, (_, index) => {
    const monday = new Date(now);
    monday.setDate(monday.getDate() - index * 7);
    const day = monday.getDay();
    monday.setDate(monday.getDate() - day + (day === 0 ? -6 : 1));
    const weekStart = monday.toISOString().split("T")[0];
    const friday = new Date(monday.getTime() + 4 * 86400000);
    const number = weekNumber(weekStart);
    const short = (value: Date, year = false) => value.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", ...(year ? { year: "numeric" as const } : {}) });
    return { weekStart, weekNumber: number, label: `KW${number} (${short(monday)} – ${short(friday, true)})` };
  });
}

function moodLabel(value: number | null) {
  if (!value) return "—";
  const emojis: Record<number, string> = { 1: "😞", 2: "😕", 3: "😐", 4: "🙂", 5: "😄" };
  return `${emojis[Math.round(value)] ?? "😐"} ${value}`;
}

export function createReportModule(http: HttpPort, download: DownloadPort, token: string, now = () => new Date()) {
  return {
    async load(classId: string, weekStart?: string): Promise<ReportView> {
      const weeks = lastFourWeeks(now());
      const selectedWeek = weeks.find((week) => week.weekStart === weekStart) ?? weeks[0];
      const data = await http.request<RawReport>({ path: `/classes/${classId}/report/weekly?week_start=${selectedWeek.weekStart}`, token });
      const summary = data.summary;
      return {
        weeks,
        selectedWeek,
        className: data.class_name,
        empty: summary.total_checkins === 0 && summary.total_checkouts === 0,
        summary: {
          totalStudents: summary.total_students,
          totalCheckins: summary.total_checkins,
          totalCheckouts: summary.total_checkouts,
          checkinRatePercent: summary.checkin_rate_percent,
          averageMood: summary.avg_mood,
          moodLabel: moodLabel(summary.avg_mood),
          treeLevel: summary.tree_level,
          praiseMessages: summary.new_praise_messages,
          unreadConcerns: summary.unread_concerns
        }
      };
    },

    async recipients(classId: string): Promise<ReportRecipient[]> {
      const data = await http.request<{ emails?: RawRecipient[] }>({ path: `/parents/${classId}/emails`, token });
      return (data.emails ?? []).map(({ id, name, email }) => ({ id, name, email }));
    },

    async execute(classId: string, command: ReportCommand): Promise<ReportCommandResult> {
      if (command.type === "download") {
        const result = await download.download({ path: `/classes/${classId}/report/weekly/download?week_start=${command.weekStart}`, token, fallbackName: "reflexo-bericht.html" });
        return { type: "downloaded", filename: result.filename };
      }
      if (command.type === "add-recipient") {
        const data = await http.request<{ entry: RawRecipient }>({ path: `/parents/${classId}/emails`, method: "POST", token, body: { name: command.name || null, email: command.email } });
        return { type: "recipient-added", recipient: { id: data.entry.id, name: data.entry.name, email: data.entry.email } };
      }
      if (command.type === "delete-recipient") {
        await http.request({ path: `/parents/${classId}/emails/${command.recipientId}`, method: "DELETE", token });
        return { type: "recipient-deleted" };
      }
      const data = await http.request<{ sent: number; message?: string; placeholder?: boolean }>({
        path: `/parents/${classId}/send-report`,
        method: "POST",
        token,
        body: {
          recipients: command.recipients.map(({ name, email }) => ({ name, email })),
          weekStart: command.weekStart,
          kw: command.weekNumber
        }
      });
      return { type: "sent", sent: data.sent, message: data.message ?? `Bericht an ${data.sent} Empfänger gesendet ✓`, placeholder: data.placeholder === true };
    }
  };
}

export type ReportModule = ReturnType<typeof createReportModule>;
